import React from 'react';
import {
  DIMENSIONS,
  SELF_ID,
  QUESTIONS,
  EXP_FOLLOWUP,
  SECTOR_Q,
  SIZE_Q,
  SECTOR,
  SIZE,
  ARCHETYPES,
  INSIGHTS,
  CAPACITY_FLAG_INSIGHT,
  REGROUPER,
  buildReport,
} from '@air/shared';
import { useAssessment } from './hooks/useAssessment';
import { submitAssessment, sendReportEmail } from './api/client';
import { Masthead } from './components/Masthead';
import { Btn, Eyebrow, ProgressBar, OptionList, DimensionIcon } from './components/atoms';
import { SignalMeter, Quadrant, Emblem } from './components/visualizations';
import { T, fonts, layout } from './theme/tokens';

const wrap = {
  minHeight: "100vh",
  background: T.cream,
  color: T.textDark,
  fontFamily: fonts.body,
  fontWeight: 400,
  padding: "clamp(18px,4vw,52px)",
  boxSizing: "border-box",
};

const shell = { maxWidth: layout.containerNarrow, margin: "0 auto" };
const h1Style = {
  fontFamily: fonts.display,
  fontWeight: 800,
  fontSize: "clamp(30px,5.5vw,48px)",
  lineHeight: 1.1,
  letterSpacing: "-1.5px",
  margin: 0,
  color: T.textDark,
};
const h2Style = {
  fontFamily: fonts.display,
  fontWeight: 700,
  fontSize: "clamp(22px,3.6vw,30px)",
  lineHeight: 1.15,
  letterSpacing: "-1px",
  margin: 0,
  color: T.textDark,
};
const leadStyle = { fontSize: 17, lineHeight: 1.85, color: T.textBody, fontWeight: 400 };
const cardStyle = {
  background: T.surface,
  border: `1px solid ${T.borderLight}`,
  borderRadius: layout.radius,
  padding: "22px 22px",
  borderLeft: `${layout.borderAccent} solid ${T.accent}`,
};

const panelStyle = {
  background: T.creamMid,
  padding: "28px 26px",
  borderRadius: layout.radius,
  marginTop: 20,
};

const DIMENSION_HINTS = {
  foundations: "Whether your processes, data and ownership are solid enough for AI to amplify, because it will amplify whatever it finds.",
  awareness: "How well you understand what AI could realistically do for an organisation like yours, beyond the hype and the demos.",
  capability: "How good your organisation actually is with AI today. Real, repeatable use, not licences owned.",
  people: "How your people are handling the shift in their work, and whether individual gains are adding up to collective progress.",
  vision: "How clear you are on what you actually want AI to do for your business. Intent, not technology.",
  capacity: "Whether anyone genuinely has the time and bandwidth to lead this. The most overlooked constraint of all.",
  value: "Whether your AI effort is tied to business results you can point to, or just generating impressive activity.",
  workforce: "Whether you've considered how AI changes roles, skills and the way your people learn and progress.",
};

function App() {
  const assessment = useAssessment();
  const {
    stage, setStage,
    selfId, setSelfId,
    sector, setSector,
    size, setSize,
    boughtTools, setBoughtTools,
    qIdx, setQIdx,
    answers,
    expFollowup, setExpFollowup,
    email, setEmail,
    consent, setConsent,
    regrouperConfirmed, setRegrouperConfirmed,
    assessmentId, setAssessmentId,
    isSubmitting, setIsSubmitting,
    submitError, setSubmitError,
    result,
    answerQuestion,
    reset,
  } = assessment;

  const [emailErr, setEmailErr] = React.useState('');

  async function handleSubmitEmail() {
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (!ok) {
      setEmailErr("Enter a valid email so we can send your full report.");
      return;
    }
    if (!consent) {
      setEmailErr("Please tick the box so we can send it and follow up.");
      return;
    }
    setEmailErr('');
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await submitAssessment({
        email: email.trim(),
        selfId,
        sector,
        size,
        boughtTools,
        answers,
        expFollowup,
        consent,
      });

      setAssessmentId(response.id);

      // Fire email send in the background — don't block the UI
      sendReportEmail(response.id).catch((err) =>
        console.error('Email send failed:', err)
      );

      // Inferred-regrouper soft-confirm step before the report
      if (result.posture === 'regrouper' && result.postureSource === 'self' && regrouperConfirmed === null) {
        setStage('regrouperConfirm');
      } else {
        setStage('report');
      }
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  const report = React.useMemo(() => {
    if (stage !== 'report' && stage !== 'gate') return null;
    return buildReport({
      result,
      sector,
      size,
      expFollowup,
      regrouperConfirmed,
    });
  }, [stage, result, sector, size, expFollowup, regrouperConfirmed]);

  return (
    <div style={wrap}>
      <div style={shell}>
        <Masthead />

        {stage === 'intro' && (
          <div>
            <Eyebrow>A 5-minute AI maturity assessment</Eyebrow>
            <h1 style={h1Style}>Find your place on the<br />AI readiness map.</h1>
            <p style={{ ...leadStyle, marginTop: 20 }}>
              Most organisations are somewhere on the AI journey, but few know exactly where.
              This short assessment helps you evaluate your organisation across eight dimensions, places you
              among the seven shapes of organisation we keep meeting, and shows you where to
              focus next to get real results.
            </p>
            <p style={leadStyle}>
              The principle behind it is simple. AI amplifies what you already have.
              If you have strong foundations, it multiplies them, if you have weak ones, it scales the friction
              and chaos just as fast. Knowing your shape is the first step to making AI pay.
            </p>
            <div style={{ ...cardStyle, marginTop: 28, marginBottom: 28 }}>
              <div style={{ fontFamily: fonts.body, fontSize: 11, fontWeight: 600, letterSpacing: "2px", color: T.accent, marginBottom: 18, textTransform: "uppercase" }}>
                The eight dimensions we assess
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px 24px" }}>
                {DIMENSIONS.map((d) => {
                  const desc = DIMENSION_HINTS[d.key];
                  return (
                    <div key={d.key} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <DimensionIcon dimension={d.key} size={18} color={T.accent} style={{ opacity: 0.6, flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.textDark, lineHeight: 1.3 }}>{d.label}</div>
                        <div style={{ fontSize: 12.5, color: T.textMid, lineHeight: 1.45, marginTop: 2 }}>{desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
              <Btn primary onClick={() => setStage('selfid')}>Start the assessment →</Btn>
              <span style={{ fontFamily: fonts.body, fontSize: 13, color: T.textLight }}>This should take about 5 minutes.</span>
            </div>
          </div>
        )}

        {stage === 'selfid' && (
          <div>
            <Eyebrow>First, a quick gut-check</Eyebrow>
            <div style={panelStyle}>
              <h2 style={{ ...h2Style, marginBottom: 10 }}>{SELF_ID.prompt}</h2>
              <p style={{ ...leadStyle, fontSize: 15, marginTop: 0, marginBottom: 22 }}>{SELF_ID.reassure}</p>
              <OptionList
                options={SELF_ID.options}
                value={selfId}
                onPick={(id) => { setSelfId(id); setTimeout(() => setStage('sector'), 200); }}
              />
            </div>
            <div style={{ marginTop: 22 }}><Btn onClick={() => setStage('intro')}>← Back</Btn></div>
          </div>
        )}

        {stage === 'sector' && (
          <div>
            <Eyebrow>A little context</Eyebrow>
            <div style={panelStyle}>
              <h2 style={{ ...h2Style, marginBottom: 10 }}>{SECTOR_Q.prompt}</h2>
              <p style={{ ...leadStyle, fontSize: 15, marginTop: 0, marginBottom: 22 }}>This sharpens the advice to your kind of organisation.</p>
              <OptionList
                options={SECTOR_Q.options}
                value={sector}
                onPick={(id) => { setSector(id); setTimeout(() => setStage('size'), 200); }}
                getText={(o) => o.label}
              />
            </div>
            <div style={{ marginTop: 22 }}><Btn onClick={() => setStage('selfid')}>← Back</Btn></div>
          </div>
        )}

        {stage === 'size' && (
          <div>
            <Eyebrow>A little context</Eyebrow>
            <div style={panelStyle}>
              <h2 style={{ ...h2Style, marginBottom: 20 }}>{SIZE_Q.prompt}</h2>
              <OptionList
                options={SIZE_Q.options}
                value={size}
                onPick={(id) => { setSize(id); setTimeout(() => setStage('bought'), 200); }}
                getText={(o) => o.label}
              />
            </div>
            <div style={{ marginTop: 22 }}><Btn onClick={() => setStage('sector')}>← Back</Btn></div>
          </div>
        )}

        {stage === 'bought' && (
          <div>
            <Eyebrow>A little context</Eyebrow>
            <div style={panelStyle}>
              <h2 style={{ ...h2Style, marginBottom: 10 }}>Have you already bought AI tools or licences?</h2>
              <p style={{ ...leadStyle, fontSize: 15, marginTop: 0, marginBottom: 22 }}>For example Microsoft Copilot, or similar. Either answer is fine.</p>
              <OptionList
                options={[{ id: 'yes', label: 'Yes, we\'ve bought tools or licences' }, { id: 'no', label: 'No, not yet' }]}
                value={boughtTools === null ? null : (boughtTools ? 'yes' : 'no')}
                onPick={(id) => { setBoughtTools(id === 'yes'); setTimeout(() => setStage('quiz'), 200); }}
                getText={(o) => o.label}
              />
            </div>
            <div style={{ marginTop: 22 }}><Btn onClick={() => setStage('size')}>← Back</Btn></div>
          </div>
        )}

        {stage === 'quiz' && (() => {
          const q = QUESTIONS[qIdx];
          const dim = DIMENSIONS.find((d) => d.key === q.dim);
          return (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontFamily: fonts.body, fontSize: 12, fontWeight: 600, color: T.accent, letterSpacing: "1.5px", textTransform: "uppercase" }}>{dim.label}</span>
                <span style={{ fontFamily: fonts.body, fontSize: 12, color: T.textLight }}>{String(qIdx + 1).padStart(2, '0')} / {QUESTIONS.length}</span>
              </div>
              <ProgressBar done={qIdx} total={QUESTIONS.length} color={T.accent} />
              <div style={panelStyle}>
                <h2 style={{ ...h2Style, fontSize: "clamp(20px,3.2vw,27px)", maxWidth: 620, marginBottom: 20 }}>{q.q}</h2>
                <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                  {q.scale.map((label, i) => {
                    const val = i + 1;
                    const active = answers[q.id] === val;
                    return (
                      <button
                        key={i}
                        onClick={() => answerQuestion(val)}
                        style={{
                          textAlign: "left",
                          display: "flex",
                          gap: 13,
                          alignItems: "center",
                          padding: "14px 16px",
                          borderRadius: layout.radius,
                          cursor: "pointer",
                          background: active ? `color-mix(in srgb, ${T.accent} 6%, ${T.surface})` : T.surface,
                          border: `2px solid ${active ? T.accent : T.borderLight}`,
                          color: T.textDark,
                          fontSize: 15,
                          fontFamily: fonts.body,
                          fontWeight: active ? 500 : 400,
                          lineHeight: 1.5,
                          transition: "background .14s,border-color .14s",
                        }}
                        onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = T.creamDark; }}
                        onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = T.surface; }}
                      >
                        <span style={{
                          fontFamily: fonts.body,
                          fontWeight: 700,
                          fontSize: 13,
                          width: 24,
                          height: 24,
                          borderRadius: layout.radius,
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          background: active ? T.accent : T.creamDark,
                          color: active ? "#fff" : T.textMid,
                        }}>{val}</span>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ marginTop: 22 }}>
                <Btn onClick={() => qIdx > 0 ? setQIdx(qIdx - 1) : setStage('bought')}>← Back</Btn>
              </div>
            </div>
          );
        })()}

        {stage === 'expf' && (
          <div>
            <Eyebrow>One more, just for you</Eyebrow>
            <h2 style={h2Style}>{EXP_FOLLOWUP.q}</h2>
            <div style={{ marginTop: 22 }}>
              <OptionList
                options={EXP_FOLLOWUP.options}
                value={expFollowup}
                onPick={(id) => { setExpFollowup(id); setTimeout(() => setStage('gate'), 200); }}
              />
            </div>
          </div>
        )}

        {stage === 'gate' && (() => {
          const isSelfRegrouper = result.posture === 'regrouper' && result.postureSource === 'self';
          const displayName = isSelfRegrouper ? 'Regrouping' : (ARCHETYPES[result.base]?.name || 'Your shape');
          const teaserPara = isSelfRegrouper ? REGROUPER.teaserSelf[0] : (ARCHETYPES[result.base]?.teaser[0] || '');
          const insightKey = isSelfRegrouper ? 'regrouper' : result.base;
          const insight = INSIGHTS[insightKey] || '';
          const sCtx = { sector: SECTOR[sector] || SECTOR.other, size: SIZE[size] || SIZE.mid };
          const couldBe = isSelfRegrouper ? REGROUPER.couldBe(sCtx) : (ARCHETYPES[result.base]?.couldBe(sCtx) || '');

          return (
            <div>
              <Eyebrow>Your archetype is ready</Eyebrow>
              <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
                <Emblem kind={isSelfRegrouper ? 'regrouper' : result.base} size={76} />
                <h2 style={h2Style}>{displayName}</h2>
              </div>
              <p style={{ ...leadStyle, fontSize: 16, marginTop: 16, marginBottom: 22 }}>{teaserPara}</p>

              <div style={{ padding: "18px 20px", borderRadius: layout.radius, background: T.creamDark, borderLeft: `${layout.borderAccent} solid ${T.accent}`, marginBottom: 26 }}>
                <div style={{ fontFamily: fonts.body, fontSize: 10.5, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: T.accent, marginBottom: 8 }}>The one thing worth your attention</div>
                <p style={{ margin: 0, fontFamily: fonts.display, fontWeight: 600, fontSize: 18, lineHeight: 1.4, color: T.textDark }}>
                  {insight}{result.flag === 'blocked' ? ' ' + CAPACITY_FLAG_INSIGHT : ''}
                </p>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, marginBottom: 22 }}>
                <div style={{ ...cardStyle, display: "flex", justifyContent: "center" }}>
                  <Quadrant d100={result.ax.direction100} r100={result.ax.readiness100} label="You" />
                </div>
                <div style={cardStyle}>
                  <div style={{ fontFamily: fonts.body, fontSize: 11, fontWeight: 600, color: T.accent, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 14 }}>Your scores across eight dimensions</div>
                  <SignalMeter c={result.c} />
                </div>
              </div>

              <h3 style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 20, marginTop: 4, marginBottom: 10, letterSpacing: "-0.5px" }}>You could be here</h3>
              <p style={{ ...leadStyle, fontSize: 15, marginBottom: 28 }}>{couldBe}</p>

              <div style={cardStyle}>
                <div style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 18, marginBottom: 6, letterSpacing: "-0.3px" }}>See the full picture</div>
                <p style={{ ...leadStyle, fontSize: 14, marginTop: 0, marginBottom: 16 }}>
                  You can see where you are and where you could get to. The emailed report explains what each of your
                  eight dimensions means for you, and sets out the themes that matter most for an organisation like yours.
                  It's the clearest view of what's worth focusing on, and where a conversation would take you furthest. We'll send it over.
                </p>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "stretch" }}>
                  <input
                    type="email"
                    value={email}
                    placeholder="you@company.com"
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmitEmail()}
                    style={{
                      flex: "1 1 230px",
                      padding: "14px 16px",
                      borderRadius: layout.radius,
                      border: `1px solid ${emailErr ? T.accent : T.borderLight}`,
                      background: T.cream,
                      color: T.textDark,
                      fontSize: 16,
                      fontFamily: fonts.body,
                      outline: "none",
                    }}
                  />
                  <Btn primary onClick={handleSubmitEmail} disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Email me the full report →'}
                  </Btn>
                </div>
                <label style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 14, fontSize: 12.5, color: T.textMid, cursor: "pointer" }}>
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 2, accentColor: T.accent }} />
                  <span>Send me my report and occasional, genuinely useful follow-ups. No spam, unsubscribe anytime. <span style={{ textDecoration: "underline" }}>Privacy policy</span>.</span>
                </label>
                {emailErr && <p style={{ color: T.accent, fontSize: 13, marginTop: 10, fontFamily: fonts.body, fontWeight: 500 }}>{emailErr}</p>}
                {submitError && <p style={{ color: T.accent, fontSize: 13, marginTop: 10, fontFamily: fonts.body, fontWeight: 500 }}>{submitError}</p>}
              </div>
            </div>
          );
        })()}

        {stage === 'regrouperConfirm' && (
          <div>
            <Eyebrow>One quick check</Eyebrow>
            <h2 style={h2Style}>{REGROUPER.confirmPrompt}</h2>
            <p style={{ ...leadStyle, marginTop: 14, marginBottom: 24 }}>
              {REGROUPER.teaserInferred[0]}
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Btn primary onClick={() => { setRegrouperConfirmed(true); setStage('report'); }}>Yes, that's us</Btn>
              <Btn onClick={() => { setRegrouperConfirmed(false); setStage('report'); }}>Not really</Btn>
            </div>
          </div>
        )}

        {stage === 'report' && report && (() => {
          const R = report;

          return (
            <div>
              <Eyebrow>Your full report</Eyebrow>
              <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
                <Emblem kind={R.isRegrouper ? 'regrouper' : result.base} size={76} />
                <h2 style={h2Style}>{R.heading}</h2>
              </div>
              {R.teaser.map((p, i) => <p key={i} style={{ ...leadStyle, fontSize: 16, marginTop: i === 0 ? 16 : 14 }}>{p}</p>)}

              {result.reconcile && (
                <div style={{ ...cardStyle, marginTop: 18 }}>
                  <p style={{ margin: 0, fontSize: 14, color: T.textMid, lineHeight: 1.55 }}>
                    You saw yourself as <b style={{ color: T.textDark }}>{SELF_ID.options.find(o => o.id === result.reconcile.selfId)?.text.toLowerCase().replace(/\.$/, '')}</b>. The answers point a little more toward the picture above. Both are worth holding in mind, the gap between how it feels and what the answers show is often the most useful thing to talk about.
                  </p>
                </div>
              )}

              <div style={{ ...cardStyle, marginTop: 22 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16, flexWrap: "wrap", gap: 6 }}>
                  <span style={{ fontFamily: fonts.body, fontSize: 11, fontWeight: 600, color: T.accent, letterSpacing: "2px", textTransform: "uppercase" }}>Your archetype</span>
                  <span style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 16, color: T.accent }}>{R.isRegrouper ? 'Regrouping' : (ARCHETYPES[result.base]?.name || '')}</span>
                </div>
                <SignalMeter c={result.c} />
                <p style={{ fontSize: 14, color: T.textMid, lineHeight: 1.55, marginTop: 18, marginBottom: 0, fontStyle: "italic" }}>{R.signalNote}</p>
              </div>

              <div style={{
                marginTop: 30,
                padding: "28px 26px",
                borderRadius: layout.radius,
                background: T.accent,
                color: "#fff",
              }}>
                <h3 style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 22, margin: "0 0 12px", letterSpacing: "-0.5px", color: "#fff" }}>You could be here</h3>
                <p style={{ margin: 0, fontSize: 16, lineHeight: 1.7, color: "rgba(255,255,255,0.92)" }}>{R.couldBe}</p>
              </div>

              <h3 style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 21, marginTop: 30, marginBottom: 14, letterSpacing: "-0.5px" }}>{R.themesHeading}</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {R.themes.map((t, i) => {
                  const dim = DIMENSIONS.find((d) => d.key === t.dim);
                  const iconKey = dim?.key || "capability";
                  return (
                    <div key={i} style={cardStyle}>
                      <div style={{ display: "flex", gap: 11, alignItems: "center", marginBottom: 7 }}>
                        <DimensionIcon dimension={iconKey} size={18} color={T.accent} style={{ flexShrink: 0, opacity: 0.4 }} />
                        <span style={{ fontFamily: fonts.display, fontSize: 24, fontWeight: 800, color: T.accent, opacity: 0.4, letterSpacing: "-2px" }}>{String(i + 1).padStart(2, '0')}</span>
                        <span style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 16.5, lineHeight: 1.25, letterSpacing: "-0.3px" }}>{t.title}</span>
                      </div>
                      <p style={{ margin: 0, fontSize: 14, color: T.textMid, lineHeight: 1.6 }}>{t.body}</p>
                    </div>
                  );
                })}
              </div>

              <div style={{ marginTop: 30, padding: "26px 24px", borderRadius: layout.radius, background: T.creamDark, borderLeft: `${layout.borderAccent} solid ${T.accent}` }}>
                <h3 style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 22, margin: "0 0 10px", letterSpacing: "-0.5px" }}>This is the 5-minute read. {R.cta.line}</h3>
                <p style={{ ...leadStyle, fontSize: 15, marginTop: 0, marginBottom: 20 }}>{R.cta.body} If this resonated, we'll be in touch to explore what it means for you.</p>
                <Btn onClick={reset}>Retake</Btn>
              </div>
              <p style={{ textAlign: "center", marginTop: 24, fontFamily: fonts.body, fontSize: 12, color: T.textLight }}>neil@neildunlopconsulting.com · neildunlopconsulting.com</p>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default App;
